/**
 * Pure helpers for AsksSection (kept out of the .tsx so the component file
 * exports only components — react-refresh rule — and the split is testable
 * without a DOM).
 */
import { isDraft } from './askKind';
import { byAttention } from '../portal/dataRequestThread';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';

/**
 * Merlin's unsent drafts first (they are the only ones that need a decision
 * before anything happens), then live asks with unseen answers on top, then
 * the dismissed ones folded away.
 */
export function splitAsks(
  requests: DoorDataRequestRead[],
  /** Leave out Merlin's drafts for a Do next step: the desk's "Ask" button
   *  owns those (kanban d3ec3001); a draft filed from chat still lists.
   *  `hideIds` leaves out exactly the drafts a desk button on screen sends
   *  (a step draft on the reader's own desk has no button, so it lists). */
  { hideStepDrafts = false, hideIds }: { hideStepDrafts?: boolean; hideIds?: ReadonlySet<string> } = {},
): {
  drafts: DoorDataRequestRead[];
  live: DoorDataRequestRead[];
  dismissed: DoorDataRequestRead[];
} {
  // A pass-along held for Merlin's check is `proposed` but not a draft: it is
  // out of the sender's hands and lists with the live asks (kanban 196e9d9b).
  const drafts = requests
    .filter(isDraft)
    .filter((r) => !(hideStepDrafts && isStepDraft(r)) && !hideIds?.has(r.id))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  const live = requests
    .filter((r) => r.status !== 'cancelled' && !isDraft(r))
    .sort(byAttention);
  const dismissed = requests.filter((r) => r.status === 'cancelled');
  return { drafts, live, dismissed };
}

/** A draft Merlin proposed for a thread's Do next step (not from chat). */
export function isStepDraft(r: Partial<Pick<DoorDataRequestRead, 'status' | 'thread_id' | 'step_no' | 'kind'>>): boolean {
  return isDraft(r) && !!r.thread_id && r.step_no != null;
}

/** What "Review and send" did: opened the draft's Ask dialog (a Do next
 *  desk's button owns it), put the draft card in front of the reader (the
 *  asks list), or only reached the list (`fallbackId`); 'none' when the
 *  page has none of them. */
export type RevealResult = 'opened_draft' | 'scrolled_to_draft' | 'scrolled_to_list' | 'none';

/**
 * Take the reader to a draft's Send (UX round 2026-10-27: on QT-10062 the
 * draft to Rich was filed on Jess's own Do next step, which has no Ask
 * button, so "Review and send" scrolled to Do next and stopped there, with
 * no draft and no Send in sight). In order: the desk button that sends this
 * very draft (it is pressed, which opens the draft with its Send), the
 * draft's own card in the asks list, then the list itself.
 */
export function revealDraft(
  draft: Partial<Pick<DoorDataRequestRead, 'id'>>,
  fallbackId = 'case-asks',
): RevealResult {
  const id = draft.id ?? '';
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '');
  const desk = id ? document.querySelector<HTMLElement>(`[data-draft-ids~="${escaped}"]`) : null;
  const card = id ? document.getElementById(`draft-${id}`) : null;
  const el = desk ?? card ?? document.getElementById(fallbackId);
  if (!el) return 'none';
  // Merlin's write-up folds away once a case exists; open the fold first.
  const fold = el.closest('details');
  if (fold && !fold.open) fold.open = true;
  el.scrollIntoView?.({ behavior: 'smooth', block: desk ? 'center' : 'start' });
  if (desk) {
    desk.click();
    return 'opened_draft';
  }
  if (card) {
    card.focus?.({ preventScroll: true });
    return 'scrolled_to_draft';
  }
  return 'scrolled_to_list';
}

/** The asks list's line when Do next owns every draft and nothing else is
 *  live: it used to say "Every ask here was dismissed" while Merlin's drafts
 *  waited under Do next (UX round 2026-10-20). */
export function hiddenDraftsLine(hidden: number): string | null {
  if (hidden === 0) return null;
  return hidden === 1
    ? 'Nothing has gone out yet. One draft from Merlin waits under Do next for your Send.'
    : `Nothing has gone out yet. ${hidden} drafts from Merlin wait under Do next for your Send.`;
}

/** "Roblyn Jericho" / "Emily Adams and Laura Caswell" / "A, B and C". */
export function draftRecipients(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
